import TextInput from '@zoocasa/node-kit/components/controls/text-input';
import { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import Button from 'components/control/button';
import { homepageIds, searchFilterIds } from 'constants/test-constants';
import { IMMERSIVE_SEARCH_PORTAL_ID } from 'constants/HTMLAttributes';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { buildClassName } from 'utils/build-class-name';
import { useSearchBar } from '../useSearchBar';
import styles from './style.module.scss';
import SearchIconLong from 'components/icon/search-icon-long';
import React from 'react';
import { useSearchBarTracking } from 'hooks/use-search-bar-tracking';
import { useIsMobile } from 'hooks/use-size-class';
import CloseIcon from 'components/icon/close-icon';

import type SearchPrediction from 'data/search-predictions';

interface Props {
  fullWidth?: boolean;
  isInPanel?: boolean;
  includeSearchIcon?: boolean;
  className?: string;
  label?: string;
  onLocationSearchClick?: (searchPrediction: SearchPrediction) => void;
  onSearchResultSelected?: (searchPrediction: SearchPrediction) => void;
  forceModal?: boolean;
  closeButtonPlacement?: 'left' | 'right';
  closeButtonTrigger?: React.ReactElement;
  overlayClassName?: string;
}

const ImmersiveSearch = ({ fullWidth = false, isInPanel = false, includeSearchIcon = false, className, label, onLocationSearchClick, onSearchResultSelected, forceModal = false, closeButtonPlacement = 'right', closeButtonTrigger, overlayClassName }: Props) => {
  const {
    isLocationDropdownActive,
    SuggestedLocationDropdown,
    textInputRef,
    searchPlaceholder,
    isHomeAppraisal,
    isExpTenant,
    disablePopularSearches,
    searchPredictions:{
      activePrediction,
      setActivePrediction,
      isLoadingSearchPredictions,
      searchPredictions,
      searchAgentPredictions,
      isTyping,
    },
    locationQuery,
    setLocationQuery,
    doOnSearchResultClicked,
    hideSearchSuggestions,
    doOnSearchBarClicked,
    isHomePage,
    onSearchPage,
    doOnSearchButtonClicked,
  } = useSearchBar({ isInPanel, forceModal });

  const { handleFocus, handleInput, handleBlur, markSearchAsSubmitted } = useSearchBarTracking({
    textInputRef,
    locationQuery,
    isHomePage,
    onSearchPage,
    isInPanel,
  });

  const isMobile = useIsMobile();

  const handleSearchResultClick = (searchPrediction: SearchPrediction) => {
    markSearchAsSubmitted();
    onSearchResultSelected?.(searchPrediction);
    if (onLocationSearchClick) {
      onLocationSearchClick(searchPrediction);
      hideSearchSuggestions();
    } else {
      doOnSearchResultClicked(searchPrediction);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      markSearchAsSubmitted();
      if (activePrediction) {
        onSearchResultSelected?.(activePrediction as SearchPrediction);
      }
      if (onLocationSearchClick && activePrediction) {
        onLocationSearchClick(activePrediction as SearchPrediction);
        hideSearchSuggestions();
      } else {
        doOnSearchButtonClicked(locationQuery);
      }
    }
  };

  return (
    <div
      className={buildClassName(className, styles.component, styles['exp-theme'], isHomeAppraisal && styles['home-appraisal'], fullWidth && styles['full-width'])}
      data-testid={homepageIds.homepageSearchbar}>
      <div className={styles['exp-wrapper']} ref={useOutsideClickHandler(() => hideSearchSuggestions())}>
        <>
          <TextInput
            name="search-fake"
            placeholder={label || searchPlaceholder}
            onClick={() => {
              if (doOnSearchBarClicked) doOnSearchBarClicked();
              textInputRef.current?.focus();
            }}
            autoComplete='off'
            data-isopen={isLocationDropdownActive.toString()}
            data-testid={searchFilterIds.searchbar+'test'}
          />
          { includeSearchIcon && (
            <button
              type="button"
              className={styles['search-circle-btn']}
              aria-label="Search"
              onClick={() => {
                if (doOnSearchBarClicked) doOnSearchBarClicked();
                textInputRef.current?.focus();
              }}
            >
              <SearchIconLong className={styles['search-icon-long']}/>
            </button>
          )}
        </>

        {(isLocationDropdownActive && SuggestedLocationDropdown) && typeof document !== 'undefined' && createPortal(
          <div id={IMMERSIVE_SEARCH_PORTAL_ID} className={buildClassName(styles['screen-take-over'], overlayClassName)} onClick={hideSearchSuggestions}>
            <div className={styles['full-screen-search-wrapper']} onClick={e => e.stopPropagation()}>
              <Button
                label={closeButtonTrigger || <CloseIcon />}
                className={closeButtonPlacement === 'right' ? styles.close : ''}
                theme={closeButtonPlacement === 'right' ? 'primary' : undefined}
                onClick={() => hideSearchSuggestions()}
              />
              <div className={buildClassName(styles['search-wrapper'], isExpTenant && styles['exp-wrapper'])}>
                <TextInput
                  ref={textInputRef}
                  name="search"
                  placeholder={searchPlaceholder}
                  value={locationQuery}
                  onValueChange={value => {
                    setLocationQuery(value);
                    handleInput(value);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoComplete='off'
                  autoFocus={true}
                  data-isopen={isLocationDropdownActive.toString()}
                  data-testid={searchFilterIds.searchbar+'test'}
                  className={isMobile ? styles['mobile-input'] : ''}
                />
              </div>
              <SuggestedLocationDropdown
                fullWidth
                isLoading={isLoadingSearchPredictions}
                searchPredictions={searchPredictions}
                setActivePrediction={setActivePrediction}
                onClick={handleSearchResultClick}
                isActive={isLocationDropdownActive}
                className={styles['suggested-location-dropdown']}
                isAdvancedSearch={true}
                locationQuery={locationQuery}
                isTyping={isTyping}
                disablePopularSearches={!!disablePopularSearches}
                searchAgentPredictions={searchAgentPredictions}
              />
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ImmersiveSearch;
